import { FormikProvider, useFormik } from "formik";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { noop } from "shared/helpers";
import { Loader, Page } from "shared/ui";
import { useExecuteReportsData, useExecuteReportsRequests } from "shared/hooks";
import * as Yup from "yup";
import {
  clearFormikValueOnReportChange,
  clearFormikValuesOnOrderChange,
} from "shared/adapters";
import { initialExecuteReportValues } from "shared/constants";
import {
  isXlsOnlyReport,
  isXlsxPreviewOnlyReport,
} from "shared/helpers/reports";
import { ReportFormatEnum } from "shared/api";
import { ExecuteReportValues } from "shared/types";
import {
  EmptyReportsMessage,
  ExecuteReportForm,
  PdfReport,
  ReportsSidebar,
  ExcelViewer,
} from "./ui";

interface ExecuteReportProps {}

const ExecuteReport: FC<ExecuteReportProps> = () => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(
    null
  );
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(
    null
  );
  const [selectedProductCardIds, setSelectedProductCardIds] = useState<
    number[]
  >([]);
  const [showAll, setShowAll] = useState<boolean>(false);

  const [validationSchema, setValidationSchema] =
    useState<Yup.AnyObjectSchema>();

  const onOrderChange = (value: number | null) => {
    setSelectedOrderId(value);
  };

  const handleOrderChange = (value: number | null) => {
    setSelectedOrderId(value);
  };

  const onMaterialChange = (value: number | null) => {
    setSelectedMaterialId(value);
  };

  const onCompanyChange = (value: number | null) => {
    setSelectedCompanyId(value);
    setSelectedProductCardIds([]);
  };

  const onProductCardChange = (value: number[]) => {
    setSelectedProductCardIds(value);
  };

  const reportsFormik = useFormik<ExecuteReportValues>({
    initialValues: initialExecuteReportValues,
    validationSchema,
    onSubmit: noop,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });

  const { values, setFieldValue, isValid } = reportsFormik;

  const {
    selectedReport,
    groupedQuests,
    isLoadingReports,
    isLoadingOrders,
    selectedFilter: {
      isMaterialItem,
      isPackaging,
      isMultiplePackaging,
      isOrderType,
      isMultipleOrderType,
      isNotSupportedYetType,
      isReelLabResultType,
      isReelLabResultMultipleType,
      isMultipleQualityType,
      isMultipleItemType,
      isMultipleOrderDaily,
      isDriggingRigg,
      isStockType,
      isMultipleOrdersAndPackaging,
      isMultipleCompanyPackaging,
      isQualityAdhesion,
    },
    adaptedOrders,
    onOrderSearch,
    conditionSchemaMap,
    isEmptyReportsResponse,
  } = useExecuteReportsData({
    values,
    showAll,
  });

  useEffect(() => {
    reportsFormik.validateForm();
  }, [values]);

  const { executeReports, getReportData, contextHolder, pdfUrl, clearPdfUrl } =
    useExecuteReportsRequests({
      values,
      selectedReport,
    });

  useMemo(() => {
    const matchedSchema = conditionSchemaMap.find(
      (item) => item.condition
    )?.schema;

    if (matchedSchema) {
      setValidationSchema(matchedSchema);
    }

    return null;
  }, [
    isMaterialItem,
    isPackaging,
    isMultiplePackaging,
    isOrderType,
    isMultipleOrderType,
    isReelLabResultType,
    isReelLabResultMultipleType,
    isMultipleQualityType,
    isMultipleItemType,
    isMultipleOrderDaily,
    isDriggingRigg,
    isStockType,
    isMultipleOrdersAndPackaging,
    isMultipleCompanyPackaging,
    isQualityAdhesion,
  ]);

  //  Clear filters values when report (and report type) is changed
  //  Prevent useless values for export request; reset report format to PDF
  //  (XLSX is preselected for reports that are always previsualized as XLSX;
  //  XLS for the custom carbone types whose backend only ever emits XLS)
  //  Order matters: isXlsxPreviewOnlyReport is also true for XLS-only reports,
  //  so the narrower check has to come first.
  useEffect(() => {
    if (values?.id) {
      let defaultReportFormat = ReportFormatEnum.PDF;

      if (isXlsOnlyReport(selectedReport)) {
        defaultReportFormat = ReportFormatEnum.XLS;
      } else if (isXlsxPreviewOnlyReport(selectedReport)) {
        defaultReportFormat = ReportFormatEnum.XLSX;
      }

      clearFormikValueOnReportChange(
        setFieldValue,
        handleOrderChange,
        defaultReportFormat
      );
      clearPdfUrl();
    }
  }, [values?.id]);

  //  Clear filters values when selectedOrderId  is changed
  //  Prevent useless values for export request
  useEffect(() => {
    clearFormikValuesOnOrderChange(setFieldValue, onMaterialChange);
  }, [selectedOrderId]);

  if (isLoadingReports) {
    return <Loader type="fullscreen" />;
  }

  return (
    <>
      {contextHolder}
      <FormikProvider value={reportsFormik}>
        <Page title={t("title")}>
          {isEmptyReportsResponse ? (
            <EmptyReportsMessage />
          ) : (
            <div className="w-full flex gap-x-5">
              <ReportsSidebar
                groupedQuests={groupedQuests}
                pdfUrl={pdfUrl}
                clearPdfUrl={clearPdfUrl}
                showAll={showAll}
                setShowAll={setShowAll}
              />

              <div className="flex flex-col gap-y-2 w-full">
                <ExecuteReportForm
                  isLoadingOrders={isLoadingOrders}
                  isMultipleOrderDaily={isMultipleOrderDaily}
                  isMaterialItem={isMaterialItem}
                  isNotSupportedYetType={isNotSupportedYetType}
                  adaptedOrders={adaptedOrders}
                  onOrderSearch={onOrderSearch}
                  selectedOrderId={selectedOrderId}
                  onOrderChange={onOrderChange}
                  isPackaging={isPackaging}
                  isMultiplePackaging={isMultiplePackaging}
                  isOrderType={isOrderType}
                  isMultipleOrderType={isMultipleOrderType}
                  executeReports={executeReports}
                  isValid={isValid}
                  validationSchema={validationSchema}
                  getReportData={getReportData}
                  selectedReport={selectedReport}
                  pdfUrl={pdfUrl}
                  clearPdfUrl={clearPdfUrl}
                  isReelLabResultType={isReelLabResultType}
                  selectedMaterialId={selectedMaterialId}
                  onMaterialChange={onMaterialChange}
                  isReelLabResultMultipleType={isReelLabResultMultipleType}
                  selectedCompanyId={selectedCompanyId}
                  onCompanyChange={onCompanyChange}
                  selectedProductCardIds={selectedProductCardIds}
                  onProductCardChange={onProductCardChange}
                  isMultipleQualityType={isMultipleQualityType}
                  isMultipleItemType={isMultipleItemType}
                  isDriggingRigg={isDriggingRigg}
                  isStockType={isStockType}
                  isMultipleOrdersAndPackaging={isMultipleOrdersAndPackaging}
                  isMultipleCompanyPackaging={isMultipleCompanyPackaging}
                  isQualityAdhesion={isQualityAdhesion}
                />

                {isXlsxPreviewOnlyReport(selectedReport) ||
                values.report_format === "xlsx" ||
                values.report_format === "xls" ? (
                  <ExcelViewer excelUrl={pdfUrl} />
                ) : (
                  <PdfReport pdfUrl={pdfUrl} />
                )}
              </div>
            </div>
          )}
        </Page>
      </FormikProvider>
    </>
  );
};

export { ExecuteReport };
